import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { alpha, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles/createTheme";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { paths } from "src/paths";

const NAVY = "#071525";

const outlinedButtonSx = (theme: Theme) => ({
  mt: 3,
  alignSelf: "flex-start" as const,
  textTransform: "none" as const,
  fontWeight: 600,
  px: 3.5,
  py: 1.25,
  borderRadius: 2,
  color: theme.palette.text.primary,
  borderColor: theme.palette.divider,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
});

interface HomeSolutionsProps {
  onViewAll: () => void;
}

const cellSx = {
  minHeight: { xs: 260, md: 320 },
  height: "100%",
  overflow: "hidden",
  border: "1px solid rgba(7,21,37,0.08)",
  borderRadius: 3,
};

const frameSx = {
  borderRadius: 1.5,
  overflow: "hidden",
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
  bgcolor: "common.white",
};

/** Product collage: portal + live Nightscout + percentile report */
const ProductCollage: FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...cellSx,
        position: "relative",
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        backgroundImage: `linear-gradient(
          145deg,
          ${alpha(theme.palette.primary.main, 0.06)} 0%,
          ${theme.palette.grey[100]} 45%,
          ${alpha(theme.palette.common.black, 0.03)} 100%
        )`,
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: "100%",
          minHeight: { xs: 220, md: 260 },
        }}
      >
        {/* Main: Opensource.clinic portal */}
        <Box
          sx={{
            ...frameSx,
            position: "absolute",
            left: 0,
            top: "4%",
            width: { xs: "72%", md: "70%" },
            height: { xs: "78%", md: "82%" },
            zIndex: 1,
          }}
        >
          <Box
            component="img"
            src="/assets/dashboard/dashboard1.png"
            alt="Opensource.clinic portal with glucose charts and heatmaps"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top left",
            }}
          />
        </Box>

        {/* Live Nightscout monitoring */}
        <Box
          sx={{
            ...frameSx,
            position: "absolute",
            right: 0,
            top: 0,
            width: { xs: "38%", md: "40%" },
            height: { xs: "48%", md: "52%" },
            zIndex: 2,
            bgcolor: "#0a0a0a",
            borderColor: "rgba(255,255,255,0.08)",
            boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
          }}
        >
          <Box
            component="img"
            src="/assets/covers/nightscoutmonitoringlife.png"
            alt="Live Nightscout glucose monitoring"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top center",
            }}
          />
        </Box>

        {/* Percentile / data visualisation */}
        <Box
          sx={{
            ...frameSx,
            position: "absolute",
            right: { xs: "2%", md: "4%" },
            bottom: 0,
            width: { xs: "48%", md: "46%" },
            height: { xs: "42%", md: "40%" },
            zIndex: 3,
          }}
        >
          <Box
            component="img"
            src="/assets/covers/perc-graph.png"
            alt="Glucose percentile report from the portal"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const HomeSolutions: FC<HomeSolutionsProps> = ({ onViewAll }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        color: NAVY,
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          component="h2"
          variant="h3"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: "bold",
            mb: 2,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          {t("The one-stop shop for your loop.")}
        </Typography>
        <Typography variant="h5" sx={{ mb: 6, fontWeight: 500 }}>
          {t(
            "We offer you the technical and medical support you need to set up your loop."
          )}
        </Typography>

        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <Box
              sx={{
                ...cellSx,
                bgcolor: theme.palette.common.white,
                p: { xs: 4, md: 5 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 500 }}>
                {t("Medical coaching")}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(7,21,37,0.72)",
                  flexGrow: 1,
                  lineHeight: 1.7,
                }}
              >
                {t(
                  "Looping can make life with type 1 diabetes calmer and healthier. We help you set it up and fine tune it to your body, step by step, guided by an experienced doctor. So you will have better glucose control, with medical professionals at your side. You don’t need to be technical to get started."
                )}
              </Typography>
              <Button
                href={
                  "https://cal.com/team/opensourcelinic/onboarding?month=2026-11"
                }
                variant="outlined"
                size="large"
                sx={outlinedButtonSx(theme)}
              >
                {t("Book a free intake now")}
              </Button>
            </Box>
          </Grid>

          <Grid xs={12} md={6}>
            <Box
              component="img"
              src="/assets/landingspage/l2.webp"
              alt="People learning together at a coaching workshop"
              sx={{
                ...cellSx,
                display: "block",
                width: "100%",
                objectFit: "cover",
              }}
            />
          </Grid>

          <Grid xs={12} md={6}>
            <ProductCollage />
          </Grid>

          <Grid xs={12} md={6}>
            <Box
              sx={{
                ...cellSx,
                bgcolor: theme.palette.common.white,
                p: { xs: 4, md: 5 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 500 }}>
                {t("Technical support")}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(7,21,37,0.72)",
                  flexGrow: 1,
                  lineHeight: 1.7,
                }}
              >
                {t(
                  "Looping combines apps, a glucose sensor and an insulin pump, which can feel complex at first. Support covers the full setup, clear explanations of each step and regular software updates. When questions or problems come up, help is available to find a solution, at a pace that suits each user."
                )}
              </Typography>
              {/* <Box component="ul" sx={{ pl: 3, pb: 2, mt: 2, mb: 0 }}>
                <li>
                  <Typography variant="body1">
                    <strong>Nightscout</strong> hosting on NEN7510 certified
                    servers
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    Build <strong>AndroidAPS</strong> and <strong>Trio</strong>{" "}
                    without Android Studio and GitHub
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    Upload FreeStyle Libre data directly to{" "}
                    <strong>Nightscout</strong> with your LibreLinkUp account
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    Visualize your glucose data with extra{" "}
                    <strong>data visualisations</strong> in the{" "}
                    Opensource.clinic portal
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Chat</strong> with your diabetes data with (beta)
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    Inform caretakers, family members and friends about your
                    health with <strong>messaging</strong> (beta)
                  </Typography>
                </li>
              </Box> */}

              <Button
                href={paths.nightscout}
                variant="outlined"
                size="large"
                sx={outlinedButtonSx(theme)}
              >
                {t("Get your nightscout")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
